import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DashboardService } from '../services/dashboard.service';
import {
  catchError,
  debounceTime,
  finalize,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
  tap,
  timer,
} from 'rxjs';
import { GridsterConfig, Gridster } from 'angular-gridster2';
import { DashboardItem } from '../models/DashboardItem';
import { ActivatedRoute } from '@angular/router';
import { DashboardDto, OrganizationDto } from '../../../../api-main';
import { TranslateModule } from '@ngx-translate/core';
import { DashboardItemComponent } from '../dashboard-item/dashboard-item.component';
import { ItemSettingsComponent } from './item-settings/item-settings.component';
import { DashboardHeaderComponent } from './dashboard-header/dashboard-header.component';
import { NgOptimizedImage, AsyncPipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { DateDisplayComponent } from '../../../shared/components/date-display/date-display.component';
import { PrivilegesService } from '../../../shared/services/privileges.service';
import { OrganizationService } from '../../organization/organization.service';
import { WaitService } from '../../../shared/services/wait.service';
import { MatCard, MatCardContent, MatCardFooter, MatCardHeader } from '@angular/material/card';
import { SkeletonComponent } from '../../../shared/components/skeleton/skeleton.component';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    DashboardHeaderComponent,
    ItemSettingsComponent,
    Gridster,
    DashboardItemComponent,
    NgOptimizedImage,
    MatIconModule,
    TranslateModule,
    DateDisplayComponent,
    AsyncPipe,
    MatCard,
    MatCardHeader,
    MatCardContent,
    MatCardFooter,
    SkeletonComponent,
  ],
})
export class DashboardComponent implements OnDestroy, AfterViewInit {
  protected dashboardService = inject(DashboardService);
  private route = inject(ActivatedRoute);
  private privilegesService = inject(PrivilegesService);
  private organizationService = inject(OrganizationService);
  protected waitService = inject(WaitService);

  unsubscribe: Subject<void> = new Subject<void>();

  @Input() id: number;

  @Input() isInPortal = false;

  @Input() showHeader = true;

  loading = true;

  isPublic = false;

  private loadDashboard$ = new Subject<number>();

  private loaded = false; // Flag to prevent double loading

  get dashboard(): DashboardItem[] {
    return this.dashboardService.items;
  }

  get editMode(): boolean {
    return this.dashboardService.editMode$.value;
  }

  get options(): GridsterConfig {
    return this.dashboardService.options;
  }

  get noItemsMessage() {
    return !(this.dashboardService.items?.length > 0) && !this.editMode;
  }

  organization$: Observable<OrganizationDto> = of(null).pipe(
    switchMap(() => {
      if (this.privilegesService.organization()) return of(this.privilegesService.organization());
      if (this.organizationRequested) return timer(100).pipe(switchMap(() => this.organization$));
      this.organizationRequested = true;
      return this.organizationService.getState().pipe(
        switchMap((data) =>
          data.organization_id ? this.organizationService.get(data.organization_id) : of(null),
        ),
        tap((data) => {
          this.privilegesService.organization.set(data);
        }),
        finalize(() => (this.organizationRequested = false)),
      );
    }),
    takeUntil(this.unsubscribe),
  );

  protected generatedDate: Date = null;

  private organizationRequested = false;

  constructor() {
    const route = this.route;

    this.isPublic = !!route.snapshot.data['public'];
    this.dashboardService.dashboardIsDownloading.set(false);
    this.initDashboardLoadingSubscription();
    this.route.params.pipe(takeUntil(this.unsubscribe)).subscribe((params) => {
      const id = params['id'];
      if (!id) {
        // console.log('route no id');
        return;
      }
      // ID could be provided by Input
      if (this.id === id) {
        // console.log('Already loaded');
        return;
      }
      this.id = id;
      this.loadNewDashboard(id);
    });
  }

  ngAfterViewInit() {
    // We got information about id from constructor and from input,
    // Input provide data from preview, constructor from routing,
    // to prevent double
    if (this.id && !this.loaded) this.loadNewDashboard(this.id);
  }

  private loadNewDashboard(id: number) {
    this.loaded = true;
    this.dashboardService.leavingCurrentDashboard$.next();
    this.loadDashboard$.next(id);
  }

  private initDashboardLoadingSubscription() {
    // console.log('initDashboardLoadingSubscription');
    this.loadDashboard$
      .asObservable()
      .pipe(
        tap((id) => {
          this.waitService.start();
          this.loading = true;
          this.dashboardService.clearSelectedItem();
          // console.log('Start', id);
        }),
        switchMap((id) =>
          // After loading we have to restore settings
          this.editMode
            ? this.dashboardService.restoreDashboard().pipe(
                tap(() => this.dashboardService.editMode$.next(false)),
                map(() => id),
              )
            : of(id),
        ),
      )
      .pipe(
        debounceTime(100),
        switchMap((id: number) =>
          this.dashboardService.getDashboard(id).pipe(
            catchError(() => {
              return of(null);
            }),
          ),
        ),
      )
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (dashboard: DashboardDto) => {
          this.generatedDate = new Date();
          this.loading = false;
          this.dashboardService.setDashboard(dashboard);
          this.waitService.stop();
        },
        error: () => {
          console.error('Error loading dashboard');
          this.waitService.stop();
        },
      });
  }

  async ngOnDestroy() {
    this.dashboardService.leavingCurrentDashboard$.next();
    this.dashboardService.clearDashboard();
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  protected readonly Date = Date;
}
